/* pamela-laser-registry.js
   Manifest-based registry for laser etching library files.
   Expected item shape from Render:
   {
     id, name, category, subcategory, type: "spot"|"scene",
     url
   }
*/
(function () {
  "use strict";

  // Public, no key needed -- read-only catalog of already-public laser art
  // files. Do not add the admin key back here; it also guards customer PII
  // and destructive admin actions, and this file ships to every visitor.
  const DEFAULT_API =
    "https://cm-editor-server.onrender.com/api/laser-library";

  function slugify(s) {
    return String(s || "")
      .trim()
      .toLowerCase()
      .replace(/&/g, "and")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  }

  function titleCase(s) {
    return String(s || "")
      .trim()
      .replace(/[-_]+/g, " ")
      .replace(/\s+/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
  }

  function cleanName(name) {
    let n = String(name || "").trim();
    n = n.replace(/\.(png|jpe?g|webp|gif|svg)$/i, "");
    n = n.replace(/^[a-f0-9]{8,}-/i, "");
    n = n.replace(/\s*[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}\s*$/i, "");
    n = n.replace(/\s+/g, " ").trim();
    return titleCase(n || "Laser Design");
  }

  function normalizeType(value) {
    const v = String(value || "").trim().toLowerCase();
    if (v === "scene" || v === "scenes" || v === "large") return "scene";
    return "spot";
  }

  function normalizeUrl(url) {
    const raw = String(url || "").trim();
    if (!raw) return "";
    if (/^https?:\/\//i.test(raw)) return raw;
    return "https://cm-editor-server.onrender.com" + (raw.startsWith("/") ? raw : "/" + raw);
  }

  function normalizeItem(it) {
    const type = normalizeType(it.type || it.kind || it.libraryType || it.etchingType);
    const category = titleCase(it.category || "Uncategorized");
    const subcategory = titleCase(it.subcategory || "");
    const name = cleanName(it.name || it.filename || it.original_name || "");

    return {
      id: String(it.id || it.key || it.filename || it.url || ""),
      name,
      rawName: String(it.name || it.filename || it.original_name || ""),
      type,
      category,
      categoryId: slugify(category || "Uncategorized"),
      subcategory,
      subcategoryId: slugify(subcategory),
      url: normalizeUrl(it.url || it.publicUrl || it.path || it.src || ""),
      contentType: String(it.content_type || it.contentType || ""),
      width: Number(it.width || 0) || 0,
      height: Number(it.height || 0) || 0
    };
  }

  async function loadLaserLibraryOnce() {
    if (Array.isArray(window.PAMELA_LASER_LIBRARY_ALL)) {
      return window.PAMELA_LASER_LIBRARY_ALL;
    }
    if (window.__PAMELA_LASER_LIBRARY_PROMISE) {
      return window.__PAMELA_LASER_LIBRARY_PROMISE;
    }

    const url = String(window.PAMELA_LASER_LIBRARY_URL || DEFAULT_API);
    window.__PAMELA_LASER_LIBRARY_PROMISE = fetch(url, { cache: "no-store" })
      .then(async (r) => {
        if (!r.ok) throw new Error("Laser library fetch failed: " + r.status + " " + r.statusText);
        return r.json();
      })
      .then((data) => {
        const rawItems = Array.isArray(data) ? data : (Array.isArray(data.items) ? data.items : []);
        const deduped = new Map();
        rawItems.map(normalizeItem).filter((it) => it.url).forEach((it) => {
          const key = [
            it.type,
            it.categoryId,
            it.subcategoryId,
            slugify(it.name || it.rawName || it.url)
          ].join("|");
          deduped.set(key, it);
        });
        const all = Array.from(deduped.values());
        const byType = { spot: [], scene: [] };
        const byTypeCat = { spot: {}, scene: {} };

        all.forEach((it) => {
          const type = it.type === "scene" ? "scene" : "spot";
          byType[type].push(it);
          (byTypeCat[type][it.categoryId] ||= []).push(it);
        });

        window.PAMELA_LASER_LIBRARY_ALL = all;
        window.PAMELA_LASER_LIBRARY_BY_TYPE = byType;
        window.PAMELA_LASER_LIBRARY_BY_TYPE_CAT = byTypeCat;
        window.PAMELA_LASER_LIBRARY_TYPES = [
          { id: "spot", label: "Spot Etchings" },
          { id: "scene", label: "Scene Etchings" }
        ];

        window.PAMELA_LASER_LIBRARY_CATEGORIES = {};
        ["spot", "scene"].forEach((type) => {
          window.PAMELA_LASER_LIBRARY_CATEGORIES[type] = Object.keys(byTypeCat[type])
            .sort((a, b) => a.localeCompare(b))
            .map((id) => ({
              id,
              label: byTypeCat[type][id]?.[0]?.category || titleCase(id)
            }));
        });

        return all;
      })
      .catch((e) => {
        console.error("[Pamela Laser Registry] Library load error:", e);
        window.PAMELA_LASER_LIBRARY_ALL = [];
        window.PAMELA_LASER_LIBRARY_BY_TYPE = { spot: [], scene: [] };
        window.PAMELA_LASER_LIBRARY_BY_TYPE_CAT = { spot: {}, scene: {} };
        throw e;
      });

    return window.__PAMELA_LASER_LIBRARY_PROMISE;
  }

  window.PAMELA_LASER_LIBRARY_ENSURE = async function () {
    await loadLaserLibraryOnce();
    return true;
  };

  window.PAMELA_LASER_GET_SUBCATEGORIES = function (type, categoryId) {
    const t = normalizeType(type);
    const cid = slugify(categoryId || "") || "uncategorized";
    const items = window.PAMELA_LASER_LIBRARY_BY_TYPE_CAT?.[t]?.[cid] || [];
    const map = {};
    items.forEach((it) => {
      const sub = String(it.subcategory || "").trim();
      if (!sub) return;
      const id = slugify(sub);
      if (!map[id]) map[id] = { id, label: sub };
    });
    return Object.values(map).sort((a, b) => a.label.localeCompare(b.label));
  };

  // Same reasoning as the clipart registry's prefetch: warm this in the
  // background once idle, rather than waiting for the customer to open the
  // laser library panel.
  (function prefetchLaserLibraryInBackground() {
    const kick = () => { loadLaserLibraryOnce().catch(() => {}); };
    if (typeof window.requestIdleCallback === "function") {
      window.requestIdleCallback(kick, { timeout: 4000 });
    } else {
      setTimeout(kick, 1500);
    }
  })();
})();
